import {
  BaseViewConfig,
  ReportFilterDef,
  ReportV2Definition,
} from "../../../../types/reportsV2";
import { ViewPatch } from "../hooks/useViewDraft";

/** An edit to the date range / filters, valid for any v2 layout's view. */
export type BaseViewPatch = ViewPatch<BaseViewConfig>;

export interface ToolbarFilters {
  /** Filters with a chip: listed in the view, or applied. */
  shown: ReportFilterDef[];
  /** The rest, offered by "+ Filter". */
  addable: ReportFilterDef[];
  /** Set a filter's values (an empty list removes the filter). */
  setFilter: (key: string, values: string[]) => void;
  /** Give a filter a chip. */
  showFilter: (key: string) => void;
  /** Remove an optional filter's chip and its values. */
  hideFilter: (key: string) => void;
}

const without = (filters: Record<string, string[]>, key: string) => {
  const next = { ...filters };
  delete next[key];
  return next;
};

/**
 * Which filter chips a v2 report shows and the edits behind them, shared by
 * the desktop toolbar and the phone chip row, for table and summary reports
 * alike. Applied filters always get a
 * chip, even if the view didn't list them; `hidden` filters get none (e.g.
 * staff when the API already limits the run to the caller). Every change
 * edits the draft.
 * A plain function (no hooks): call it during render.
 */
export function buildToolbarFilters(
  definition: ReportV2Definition,
  view: BaseViewConfig,
  onUpdate: (patch: BaseViewPatch) => void,
  hidden: string[] = [],
): ToolbarFilters {
  const available = definition.filters.filter((f) => !hidden.includes(f.key));
  const shown = available.filter(
    (f) => view.shown_filters.includes(f.key) || f.key in view.filters,
  );
  const addable = available.filter((f) => !shown.includes(f));

  const setFilter = (key: string, values: string[]) =>
    onUpdate((v) => ({
      filters: values.length
        ? { ...v.filters, [key]: values }
        : without(v.filters, key),
    }));

  const showFilter = (key: string) =>
    onUpdate((v) => ({ shown_filters: [...v.shown_filters, key] }));

  const hideFilter = (key: string) =>
    onUpdate((v) => ({
      shown_filters: v.shown_filters.filter((k) => k !== key),
      filters: without(v.filters, key),
    }));

  return { shown, addable, setFilter, showFilter, hideFilter };
}
